import React, { useState } from 'react';
import { ReviewInput } from '../../services/cloutReviews';
import { getApiErrorMessage } from '../../utils/errors';

// What the reviewer agrees to. A draft until counsel approves it: when the wording
// changes, change CONSENT_VERSION in backend/app/services/clout_reviews.py too.
export const REVIEW_CONSENT_TEXT =
  "This review is about my own experience as a client of this provider. I'm not employed " +
  "by, related to or paid by them, and nobody gave me anything for writing it. I agree that " +
  'CLOUT may publish it with my display name, and may analyse its text, including with AI ' +
  "services such as OpenAI, to calculate the provider's CLOUT Score.";

export const MIN_REVIEW_LENGTH = 20;
export const MAX_REVIEW_LENGTH = 5000;

interface ReviewFormProps {
  providerName: string;
  defaultName?: string;
  onSubmit: (review: ReviewInput) => Promise<void>;
  onCancel?: () => void;
}

const ReviewForm: React.FC<ReviewFormProps> = ({
  providerName,
  defaultName = '',
  onSubmit,
  onCancel,
}) => {
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [name, setName] = useState(defaultName);
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedText = text.trim();
    const trimmedName = name.trim();
    if (!rating) return setError('Choose a rating from 1 to 5 stars.');
    if (trimmedText.length < MIN_REVIEW_LENGTH)
      return setError(`Write at least ${MIN_REVIEW_LENGTH} characters.`);
    if (trimmedName.length < 2) return setError('Enter the name to show with your review.');
    if (!consent) return setError('Tick the box to confirm and agree before submitting.');

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        rating,
        review_text: trimmedText,
        reviewer_name: trimmedName,
        consent: true,
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Could not submit your review. Please try again.'));
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 font-['Open_Sans'] text-sm text-[#1f1f1f]">
      <fieldset>
        <legend className="font-semibold mb-2">Your rating of {providerName}</legend>
        <div className="flex gap-1" role="radiogroup" aria-label="Rating">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              role="radio"
              aria-checked={rating === star}
              aria-label={`${star} ${star === 1 ? 'star' : 'stars'}`}
              onClick={() => setRating(star)}
              className={`text-2xl leading-none ${star <= rating ? 'text-[#c6a664]' : 'text-[#d9d6d0]'}`}
            >
              ★
            </button>
          ))}
        </div>
      </fieldset>

      <div>
        <label htmlFor="review-text" className="block font-semibold mb-1">
          Your review
        </label>
        <textarea
          id="review-text"
          value={text}
          onChange={(e) => setText(e.target.value)}
          maxLength={MAX_REVIEW_LENGTH}
          rows={5}
          className="w-full px-4 py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
        />
        <p className="text-xs text-[#767472] mt-1">
          Share your honest experience, positive or negative. Please don't include health details
          you want to keep private, such as conditions or medications, or anyone else's personal
          information.
        </p>
      </div>

      <div>
        <label htmlFor="reviewer-name" className="block font-semibold mb-1">
          Name to show
        </label>
        <input
          id="reviewer-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={60}
          placeholder="For example, Jamie R."
          className="w-full px-4 py-2.5 border border-[#e5e5ea] rounded focus:outline-none focus:border-[#c6a664] text-base"
        />
      </div>

      <label className="flex items-start gap-2">
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1"
        />
        <span className="text-xs text-[#4a4846]">{REVIEW_CONSENT_TEXT}</span>
      </label>

      <p className="text-xs text-[#767472]">
        CLOUT checks every review before it's published. We don't remove reviews for being
        negative.
      </p>

      {error && (
        <p role="alert" className="text-red-600">
          {error}
        </p>
      )}

      <div className="flex gap-3">
        <button
          type="submit"
          disabled={submitting}
          className="px-5 py-2.5 bg-[#1f1f1f] text-white font-semibold uppercase text-xs hover:bg-[#333333] disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit review'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2.5 text-[#767472] hover:underline"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default ReviewForm;
